import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined, FileTextOutlined, RocketOutlined } from "@ant-design/icons";
import { Button, Card, Form, Input, Layout, Space, Spin, Typography, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import { LoadingContext } from "@/Context/LoadingContext";
import { UserContext } from "@/Context/UserContext";

const { Title, Text } = Typography;
const { Content } = Layout;
const { TextArea } = Input;

const NaukriJobInput = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const [checkingPlan, setCheckingPlan] = useState(true);

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const authToken = localStorage.getItem("authorization");
    const userId = localStorage.getItem("userId");
    if (!authToken || !userId) {
      navigate("/app/login?returnUrl=" + encodeURIComponent(window.location.pathname));
    }
  }, [navigate]);

  // Fetch plan info to check if user can create resume
  useEffect(() => {
    const checkPlanInfo = async () => {
      try {
        setCheckingPlan(true);
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch.get(`/user/dashboard/${userId}`, false);
        if (response.success && response.data.planInfo) {
          if (!response.data.planInfo.canCreateResume) {
            message.warning("You have reached your resume limit. Free plan allows only 3 resumes.");
            navigate("/app/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking plan info:", error);
      } finally {
        setCheckingPlan(false);
      }
    };
    checkPlanInfo();
  }, []);

  const handleBack = () => {
    navigate("/app/resume-choice");
  };

  const handleSubmit = async (values) => {
    setError("");
    startLoading();

    try {
      const response = await fetch.post("generate-resume-from-job", {
        jobTitle: values.jobTitle,
        jobDescription: values.jobDescription,
        fullName: userProfileData?.fullName || `${userProfileData?.firstName || ""} ${userProfileData?.lastName || ""}`.trim() || "",
        email: userProfileData?.email || "",
        phone: userProfileData?.mobile || "",
      });

      const resumeData = response?.data;

      if (resumeData?.resumeId) {
        updateProfileInfo({ ...userProfileData, resumeId: resumeData.resumeId });
        message.success("Resume generated successfully!");
        navigate(`/app/resume/${resumeData.resumeId}`, {
          state: { isNewResume: true },
        });
      } else {
        setError("Failed to generate resume. Please try again.");
      }
    } catch (err) {
      console.error("Error generating resume:", err);
      setError(err?.response?.data?.message || "Failed to generate resume. Please try again.");
    } finally {
      stopLoading();
    }
  };

  // Show loading while checking plan
  if (checkingPlan) {
    return (
      <AppLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Content
        style={{
          marginTop: "20px",
          marginBottom: "20px",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Back Button */}
        <div
          style={{
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-to-options-btn"
            style={{
              fontSize: "14px",
              borderColor: "#1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
              height: "36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.3s ease",
            }}
          >
            <span className="back-button-text">Back to options</span>
          </Button>
        </div>

        {/* Header Section */}
        <div
          style={{
            marginTop: "30px",
            marginBottom: "24px",
            textAlign: "center",
            width: "100%",
            padding: "0 16px",
          }}
        >
          <Title level={2} style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)" }}>
            Create Resume for Naukri Job Post
          </Title>
          <Text type="secondary" style={{ fontSize: "clamp(0.875rem, 3vw, 1rem)" }}>
            Enter your details and paste the job description from Naukri to create a tailored resume that matches
            the requirements perfectly.
          </Text>
        </div>

        {/* main card  */}
        <div style={{ width: "100%", maxWidth: "800px", padding: "0 16px" }}>
          <Card
            style={{
              borderRadius: "16px",
              border: "1px solid #e8e8e8",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              transition: "box-shadow 0.3s ease",
            }}
            styles={{
              body: {
                padding: "clamp(12px, 5vw, 22px)",
              },
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 6px 24px rgba(0, 0, 0, 0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
            }}
          >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              {/* Job Title Input */}
              <Form.Item
                label={<span style={{ fontSize: "16px", fontWeight: "600" }}>Job Title</span>}
                name="jobTitle"
                rules={[
                  {
                    required: true,
                    message: "Please enter the job title you're applying for",
                  },
                ]}
                extra="Enter the exact job title from the Naukri posting"
                style={{ marginBottom: "10px" }}
              >
                <Input
                  placeholder="e.g., Senior Software Engineer, Marketing Manager"
                  style={{
                    borderRadius: "8px",
                    fontSize: "15px",
                    padding: "4px 12px",
                    border: "1.5px solid #d9d9d9",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#1890ff";
                    e.target.style.boxShadow = "0 0 0 2px rgba(24, 144, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d9d9d9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </Form.Item>

              {/* Job Description Input */}
              <Form.Item
                label={<span style={{ fontSize: "16px", fontWeight: "600" }}>Job Description</span>}
                name="jobDescription"
                rules={[
                  {
                    required: true,
                    message: "Please paste the job description",
                  },
                ]}
                extra="Copy and paste the complete job description including responsibilities, requirements, and qualifications"
                style={{ marginBottom: "10px" }}
              >
                <TextArea
                  rows={8}
                  placeholder="Paste the complete job description from Naukri here (including company info, role details, requirements, skills, etc.)

Example format:
- Company Overview / About Us
- Job Role & Responsibilities
- What You'll Learn/Do
- Required Skills & Qualifications
- Preferred Skills (if any)
- Education Requirements
- Key Skills & Technologies

💡 Include everything from the job post - the more complete the description, the better AI can optimize your resume!"
                  style={{
                    borderRadius: "8px",
                    fontSize: "15px",
                    padding: "4px 12px",
                    border: "1.5px solid #d9d9d9",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#1890ff";
                    e.target.style.boxShadow = "0 0 0 2px rgba(24, 144, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d9d9d9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </Form.Item>

              {/* Features List */}
              <Card
                style={{
                  backgroundColor: "#f6ffed",
                  border: "1.5px solid #b7eb8f",
                  borderRadius: "12px",
                  marginTop: "5px",
                  marginBottom: "20px",
                  boxShadow: "0 2px 8px rgba(82, 196, 26, 0.1)",
                }}
                styles={{
                  body: {
                    padding: "12px 16px",
                  },
                }}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text strong style={{ fontSize: "15px", color: "#52c41a", fontWeight: "600", marginBottom: "8px" }}>
                  🚀  The Next Move
                  </Text>
                  <Space direction="vertical" size={2} style={{ width: "100%" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "4px" }}>
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", marginRight: "10px", marginTop: "2px", fontSize: "16px" }}
                      />
                      <Text style={{ color: "#666", fontSize: "14px" }}>
                      AI scans job requirements & key skills
                      </Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "4px" }}>
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", marginRight: "10px", marginTop: "2px", fontSize: "16px" }}
                      />
                      <Text style={{ color: "#666", fontSize: "14px" }}>
                      Resume customized for perfect job match
                      </Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", marginRight: "10px", marginTop: "2px", fontSize: "16px" }}
                      />
                      <Text style={{ color: "#666", fontSize: "14px" }}>
                      Optimized with ATS-approved keywords
                      </Text>
                    </div>
                  </Space>
                </Space>
              </Card>

              {/* Error Display */}
              {error && (
                <div style={{ marginBottom: "16px" }}>
                  <Text type="danger">{error}</Text>
                </div>
              )}

              {/* Submit Button */}

              <Form.Item style={{ marginBottom: 0, textAlign: "center" }}>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  icon={<RocketOutlined />}
                  style={{
                    borderRadius: "10px",
                    fontWeight: "600",
                    height: "56px",
                    fontSize: "16px",
                    paddingLeft: "32px",
                    paddingRight: "32px",
                    background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(24, 144, 255, 0.4)",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(24, 144, 255, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(24, 144, 255, 0.4)";
                  }}
                >
                Start Resume Building
                </Button>
              </Form.Item>
            </Form>
          </Card>

        </div>
      </Content>
    </AppLayout>
  );
};

export default NaukriJobInput;
